// Audit No. 0: the Audit we ran on our own outreach, in October 2026.
// Nothing here is invented. Each figure carries one of four marks:
//   counted   - taken from the list, the log or the site
//   estimated - the founder's own estimate
//   planned   - a target, not a record
//   decided   - a rule the founder set
// Sources: the outreach record kept during 6-7 October 2026 and the site's own
// logs. Change a figure only when its source changes.

export const AUDIT_META = {
  number: "No. 0",
  job: "First contact with new firms",
  company: "Nomus",
  period: "6–7 October 2026",
  by: "Mariusz Czajkowski",
  asOf: "7 October 2026",
};

export const MARKS = {
  counted: "Counted",
  estimated: "Estimated",
  planned: "Planned",
  decided: "Decided",
};

// Page 1. The answer comes first.
export const ANSWER = {
  verdict: "Worth a tool.",
  text: "At the volume we planned, writing to new firms by hand is more than one person's whole job, every month of the year. Nobody was hired for it, so it was not being done. A tool can do the research and the sending inside written rules, and leave the judgment with a person.",
  scale: [
    ["2,250–3,000", "hours a year"],
    ["1.1–1.5", "people doing nothing else"],
    ["0", "people hired to do it"],
  ],
  figures: [
    {
      value: "188–250",
      unit: "hours a month",
      label: "to do the job by hand at the planned volume",
      mark: "estimated",
    },
    {
      value: "0",
      unit: "emails sent",
      label: "before the tool, with 98 firms on the list",
      mark: "counted",
    },
    {
      value: "$4,900",
      unit: "fixed",
      label: "for the tool, as we would quote it to you",
      mark: "decided",
    },
  ],
};

// Page 2. What was measured, and how.
export const SCOPE = {
  sentence:
    "Find a firm that fits, find the person who owns the problem, write to them once with a true reason, follow up twice, and answer within a day when they write back.",
  facts: [
    ["Planned volume", "250 first emails a month", "planned"],
    ["One email by hand", "45–60 minutes, research included", "estimated"],
    ["So, by hand", "188–250 hours a month", "estimated"],
    ["People available", "One founder", "counted"],
    ["Firms on the list", "98", "counted"],
    ["Sent before the tool", "0", "counted"],
  ],
  method: [
    "We read the list, the drafts and the notes as they stood on 6 October.",
    "We counted what the list held: names, addresses, reasons to write.",
    "We asked the founder how long one email takes by hand. The answer is an estimate. Nobody had timed one.",
    "We followed one message from the website to see who was told. Nobody was.",
  ],
};

// The plan the volume comes from. A hypothesis, not a record: nothing had been
// sent when it was written.
export const PLAN = [
  ["250", "first emails a month"],
  ["about 15", "replies"],
  ["6–8", "calls"],
  ["2–3", "Audits"],
  ["1–2", "tools built"],
];

// Page 3. The job, step by step, before and with the tool.
// who: "tool" does it alone inside the rules, "approve" waits for a person's
// yes, "person" stays human, "open" is not solved yet.
export const STEPS = [
  {
    name: "Choose the firm",
    before: "A list of 98 firms, built by desk research.",
    after:
      "The same list. The tool takes the next firm that has a reason to be written to.",
    was: "ran",
    who: "tool",
  },
  {
    name: "Check it still fits",
    before: "23 of the 98 headcounts were two or more years old.",
    after: "Flagged on the list. Not rechecked yet.",
    was: "slow",
    who: "open",
  },
  {
    name: "Name the person",
    before: "86 of 98 firms named an owner or partner.",
    after: "Unchanged. A firm with no named owner is skipped.",
    was: "ran",
    who: "tool",
  },
  {
    name: "Find a published address",
    before:
      "83 firms had some way to reach them. 72 had a named owner and a way to reach them.",
    after:
      "43 owner and partner addresses confirmed on the firms' own websites by the end of day one. None guessed.",
    was: "slow",
    who: "tool",
  },
  {
    name: "Find a reason to write",
    before: "About 26 of 98 firms had a reason dated in the last 30 days.",
    after: "No recent reason, no email. The firm waits until there is one.",
    was: "slow",
    who: "tool",
  },
  {
    name: "Write the email",
    before: "A three-email sequence, drafted in a document.",
    after:
      "The first email is filled in from the approved wording. Nothing about the firm is made up.",
    was: "ran",
    who: "tool",
  },
  {
    name: "Approve",
    before: "Every email and every list of recipients waited for the founder.",
    after:
      "The wording was approved once. Daily limits were set. Anything outside that wording still waits for the founder.",
    was: "slow",
    who: "approve",
  },
  {
    name: "Send",
    before: "Nothing was sent.",
    after:
      "From the founder's own address, up to 15 new firms a working day. 9 went out on day one.",
    was: "stopped",
    who: "tool",
  },
  {
    name: "Keep the record",
    before: "No record, because nothing was sent.",
    after: "Every send is written to one log: who, when, which wording.",
    was: "stopped",
    who: "tool",
  },
  {
    name: "Follow up",
    before:
      "Planned for day 3–4 and day 8–10. Depended on someone remembering.",
    after:
      "Scheduled when the first email goes. After the second follow-up the tool stops.",
    was: "stopped",
    who: "tool",
  },
  {
    name: "Notice an answer",
    before: "A message sent through the website was stored. Nobody was told.",
    after:
      "Each new message is passed on within seconds. The whole list is read again every weekday morning, in case one was missed.",
    was: "stopped",
    who: "tool",
  },
  {
    name: "Answer, and offer a time",
    before:
      "No promise that could be kept. The calendar was open in Warsaw office hours, for buyers in the United States.",
    after:
      "A person answers within 24 hours. The calendar is open from 9am to 3pm Eastern on Tuesdays and Thursdays.",
    was: "slow",
    who: "person",
  },
];

export const WAS = {
  ran: "Ran",
  slow: "Slow or patchy",
  stopped: "Did not happen",
};

export const WHO = {
  tool: "The tool",
  approve: "You approve",
  person: "A person",
  open: "Still open",
};

// Page 4. Where the job leaked, largest first.
export const LEAKS = [
  {
    name: "More work than one person has",
    size: "188–250 h",
    mark: "estimated",
    text: "250 emails a month at 45 to 60 minutes each is more than a full working month. There is one founder, and this is one job among all the others.",
  },
  {
    name: "No reason to write",
    size: "About 72 of 98",
    mark: "counted",
    text: "Only about 26 firms had something recent and true to open with. By hand, the choice is to write anyway and sound like everyone else, or to go looking, which is most of the hour.",
  },
  {
    name: "One approver",
    size: "Every send",
    mark: "decided",
    text: "Each email and each list waited for the founder's yes. The rule was right. The queue was the problem: one person, asked 250 times a month.",
  },
  {
    name: "No address to write to",
    size: "26 of 98",
    mark: "counted",
    text: "26 firms had no named owner with a way to reach them. Finding a published address, and refusing to guess one, is slow work that looks like nothing.",
  },
  {
    name: "Nobody home when they answer",
    size: "0 told",
    mark: "counted",
    text: "A message from the website reached a database and stopped. The calendar offered European afternoons to American buyers. The best email in the world ends here.",
  },
];

// Page 5. Foundations: the same five areas the check scores, 0 to 3.
export const FOUNDATIONS = [
  {
    area: "Where it lives",
    points: 1,
    text: "A spreadsheet file, a document of drafts and a thread of notes. Three places, none of them connected.",
  },
  {
    area: "Written down",
    points: 3,
    text: "The sequence was written. So were the rules: who approves, real addresses only, log every send, stop at the first bounce.",
  },
  {
    area: "AI in use",
    points: 3,
    text: "AI already researched and drafted here, under those written rules.",
  },
  {
    area: "AI access",
    points: 2,
    text: "It could read the list and public pages. It could not send.",
  },
  {
    area: "Access and security",
    points: 2,
    text: "The sending address was set up properly and signed. The domain's policy only watched; it did not yet refuse forged mail.",
  },
];

// Page 6. What the software already allowed, before anything was built.
export const SOFTWARE = [
  {
    system: "The mailbox",
    vendor: "Hostinger",
    allows:
      "Sends from the founder's own address through the provider. No new sending service needed.",
    rule: "Stop at the first bounce or limit.",
  },
  {
    system: "The calendar",
    vendor: "Cal.com",
    allows:
      "Opening hours by weekday, a notice period, a cap on calls per day and per week, and notes filled in from the link.",
    rule: "Two calls a day, four a week.",
  },
  {
    system: "The website",
    vendor: "Cloudflare",
    allows:
      "Stores every message and can pass each new one to another system, signed so it cannot be faked.",
    rule: "Stored first, passed on second.",
  },
  {
    system: "The measuring",
    vendor: "PostHog, in the EU",
    allows: "Counts each step of a visit, only for visitors who allow it.",
    rule: "Every number is a floor, not a total.",
  },
  {
    system: "The sources",
    vendor: "Public pages",
    allows:
      "An industry list of small firms and each firm's own website. Read only.",
    rule: "Published addresses only.",
  },
];

// Page 7. The tool.
export const TOOL = [
  {
    head: "It reads",
    items: [
      "The list of firms",
      "Each firm's public pages",
      "The log of what was sent",
      "New messages from the website",
    ],
  },
  {
    head: "It prepares",
    items: [
      "A confirmed address for a named owner",
      "One true reason to write, dated this month",
      "The first email, on the approved wording",
      "Two follow-ups, on day 3–4 and day 8–10",
    ],
  },
  {
    head: "It waits for a person",
    items: [
      "Any new wording",
      "Every reply",
      "Anything the approved wording does not cover",
      "A new source of firms",
    ],
  },
  {
    head: "It does alone",
    items: [
      "Sends up to 15 first emails a working day",
      "Never more than 20 a day with follow-ups",
      "Writes every send to the log",
      "Reports each day: sent, to whom, what came back",
    ],
  },
];

export const NEVER = [
  "Guess an email address.",
  "Say something about a firm that it has not read on a public page.",
  "Write to two partners of the same firm in one week.",
  "Answer a reply. A person does that.",
];

// The rules the tool runs under, as the founder set them.
export const RULES = [
  "The founder approves the wording before anything is sent.",
  "Real, published addresses only.",
  "Every send goes in the log.",
  "Stop at the first bounce or sending limit.",
  "At most 15 new firms a working day, 20 emails in all.",
  "A daily report to the founder.",
];

// The tool as a line: what goes in, the gate, what comes out.
export const FLOW = [
  { label: "Reads", items: ["The list", "Public pages", "The log"] },
  { label: "Prepares", items: ["The address", "The reason", "The email"] },
  {
    label: "The gate",
    items: ["Approved wording", "Daily limits"],
    gate: true,
  },
  { label: "Does", items: ["Sends", "Logs", "Follows up"] },
  { label: "Reports", items: ["Daily, in plain words"] },
];
export const FLOW_ANSWER = [
  { label: "A message arrives", items: ["From the website"] },
  { label: "Stored", items: ["Before anything else"] },
  { label: "Passed on", items: ["Within seconds"] },
  { label: "A person", items: ["Answers within 24 hours"], gate: true },
  { label: "The calendar", items: ["In the buyer's hours"] },
];

// Why this, and not that. Each line is a choice we made and what it cost.
export const CHOICES = [
  {
    chose: "The founder's own mailbox",
    over: "A bulk sending service",
    why: "At 15 emails a day there is nothing to scale. Sent from a real address, a reply lands where a person already reads.",
  },
  {
    chose: "Approve the wording once",
    over: "Approve every email",
    why: "Approving each one was 250 decisions a month for one person. The risk sits in the wording and the limits, so that is where the approval went.",
  },
  {
    chose: "A reason, or no email",
    over: "Working down the list",
    why: "It leaves about 72 firms unwritten today. Without a reason, the email is the one everybody sends.",
  },
  {
    chose: "Published addresses",
    over: "Guessed ones",
    why: "A guess is usually right. When it is wrong, a stranger gets a sales email with our name on it.",
  },
  {
    chose: "15 a day",
    over: "As many as the mailbox allows",
    why: "It is one setting to raise. The honest reasons to write run out long before the mailbox does.",
  },
  {
    chose: "Store first, then alert",
    over: "Alert only",
    why: "Alerts fail. Ours did, in testing. The stored list is the record, and it is read again every morning.",
  },
  {
    chose: "A hosted relay",
    over: "The laptop it was first built on",
    why: "The first version passed alerts through a laptop. A closed lid would have lost a lead. It was moved the same afternoon.",
  },
  {
    chose: "A person answers",
    over: "An automatic reply",
    why: "Someone who writes back has earned a person. This is the one step we refuse to speed up.",
  },
];

// What it runs on. The models can be swapped; the rules, the log and the
// record stay.
export const STACK = [
  {
    layer: "The person",
    parts: [
      [
        "The founder",
        "Sets the rules, approves the wording, answers every reply.",
      ],
    ],
  },
  {
    layer: "The agents",
    parts: [
      [
        "Grok",
        "Research and outreach: builds the list, confirms addresses, finds the reason, fills the wording, sends, logs.",
      ],
      [
        "Claude",
        "Built the website, the two-minute check and the alert for new messages.",
      ],
    ],
  },
  {
    layer: "The rails",
    parts: [
      [
        "Cursor",
        "Runs the scheduled routines: the hand-off of each new message and the morning read.",
      ],
      [
        "Outer",
        "The shared record. Every rule, decision and send, readable by the founder and by every agent.",
      ],
    ],
  },
  {
    layer: "The systems",
    parts: [
      ["Hostinger", "The mailbox."],
      ["Cal.com", "The calendar."],
      ["Cloudflare", "The website, the stored messages and the relay."],
      ["PostHog", "The measuring, for visitors who allow it."],
    ],
  },
];

// What could go wrong, and the one thing that holds it.
export const RISKS = [
  [
    "The email reaches the wrong person",
    "Published owner addresses only. One partner per firm in a week.",
  ],
  [
    "It says something untrue about a firm",
    "The tool may only repeat what it read on a public page. No reason, no email.",
  ],
  [
    "The mailbox is treated as a spammer",
    "15 new firms a day. It stops itself at the first bounce or limit.",
  ],
  [
    "Someone answers and nobody sees it",
    "Stored first, passed on in seconds, read again every weekday morning.",
  ],
  [
    "The founder loses sight of what is sent",
    "One log and one daily report. Withdrawing the approval stops every send.",
  ],
  [
    "The model behind an agent changes",
    "The rules, the log and the record do not live in the model. A different one can read them tomorrow.",
  ],
];

// Page 8. Two pages against our own interest.
export const NOT_BUILT = [
  [
    "Automatic answers",
    "When someone writes back, they have earned a person. Automating that would save minutes and cost the conversation.",
  ],
  [
    "Guessed addresses",
    "Patterns like first.last@ are usually right and occasionally land on a stranger. A published address or no email.",
  ],
  [
    "More volume",
    "The cap is 15 new firms a day. Raising it is one setting. We left it, because the reason to write runs out before the list does.",
  ],
];

export const FIX_YOURSELF = [
  "Write down, on one page, who may send what without asking.",
  "Keep one list and one log. Delete the copies.",
  "Open your calendar in your buyer's working hours, not yours.",
  "Send yourself a message through your own website. See who is told, and when.",
  "Promise a reply time only after that test passes.",
];

// Page 9. The price and what it buys.
export const PRICE = {
  tool: "$4,900",
  care: "From $350 a month, optional",
  includes: [
    "The twelve steps on page 4, built as mapped",
    "The written rules and the daily limits, changeable by you",
    "One log, in your own account",
    "A daily report in plain words",
    "Tested on real sends before it is handed over",
    "Written instructions someone new can follow",
  ],
  no: "If the job had come to under ten hours a month, this page would say: do not build it. You would keep the map and the five fixes on the page before.",
};

// The proof. An Audit ends with the thing working, not with a description of it.
export const DAY_ONE = {
  intro:
    "On its first day the tool wrote to nine real firms under the rules on page 8, and a real message from the website reached a person. That is the proof. In your Audit it comes on day 14: a first version doing one real case of yours while you watch.",
  figures: [
    ["9", "first emails sent", "counted"],
    ["15", "the daily limit", "decided"],
    ["43", "owner addresses confirmed", "counted"],
    ["0", "addresses guessed", "counted"],
  ],
  found: [
    "The first version of the alert for new messages failed without telling anyone. Our own test messages caught it, and it was fixed the same day, before a real lead arrived.",
    "The website promised a reply within two working days when nobody was being told about messages at all. Once the alert worked, the promise became 24 hours.",
  ],
  open: [
    "Replies. The first emails went out on the day this was written. It is too early to say.",
    "The 23 firms whose size we have not rechecked.",
    "Whether 45–60 minutes by hand is right. Nobody timed it.",
  ],
  test: "The test we set: by 30 November 2026, five real conversations that started from this channel. If it falls short, the wording or the list is wrong, and we will say so here.",
};

export const PAGES = [
  ["answer", "The answer"],
  ["scope", "What we measured"],
  ["yours", "The same leak at your size"],
  ["steps", "The job, step by step"],
  ["leaks", "Where it leaked"],
  ["foundations", "Foundations"],
  ["software", "What the software already allowed"],
  ["tool", "The tool"],
  ["choices", "Why this, and not that"],
  ["stack", "What it runs on"],
  ["risks", "What could go wrong"],
  ["honest", "What we would not build"],
  ["price", "The price"],
  ["dayone", "The proof"],
];

// How to read each page. Shown in the margin.
export const NOTES = {
  risks:
    "If we cannot say how it fails, we do not understand it yet. Each risk has the one thing that holds it.",
  stack:
    "Named, so you can check it. The models will change. What you own is the layer they read: the rules, the log and the record.",
  choices:
    "Every tool is a set of choices. You should be able to see each one, what we turned down, and what it cost.",
  yours:
    "Our numbers are one founder's. Put in your own team and see what the same kind of job costs you. Nothing you type here is sent anywhere.",
  answer:
    "Every Audit opens with the answer. If you read one page, read this one. The rest is how we got there.",
  scope:
    "Each number says where it came from. In your Audit most are counted or timed on real cases. Ours has an estimate at its centre, because by hand the job never ran. The plan is shown so you can see where 250 comes from.",
  steps:
    "This is the page most teams have never been given: their own job, written down step by step, with what each step waits for. Switch between before and with the tool.",
  leaks:
    "Ranked by what it costs, not by what is easiest to fix. In your Audit each line is in hours a month.",
  foundations:
    "The same five areas as the two-minute check, scored on evidence instead of your answers. A tool is only built on what holds.",
  software:
    "Before proposing anything, we check what the software you already pay for can do. Often it is more than the team knows.",
  tool: "The tool as a line, then as four lists, then the rules it runs under. If something is not on this page, the tool does not do it.",
  honest:
    "Two lists that cost us work: what we refused to build, and what you can fix this week without us.",
  price:
    "One number, fixed before you decide. And the sentence you would have read if the answer were no.",
  dayone:
    "Most audits end in a recommendation. Ours ends with the thing running. On day 14 you watch a first version do one real case of yours.",
};

// The same document in the shape every Audit is stored in. A client's Audit
// is this object with their job in it; one set of parts draws both
// (components/site/AuditDoc.jsx). The server checks the shape in
// cms/auditdoc.mjs and hands this one out as the example to follow.
export const SAMPLE_DOC = {
  meta: AUDIT_META,
  answer: {
    ...ANSWER,
    scaleNote:
      "The same estimate, across a year. A full-time year is taken as 2,000 hours.",
  },
  scope: {
    ...SCOPE,
    plan: {
      title: "Where 250 comes from",
      rows: PLAN,
      note: "A plan, written before anything was sent. Only the first line drives the hours on this page.",
      mark: "planned",
    },
  },
  steps: {
    before: "Before · 6 October",
    after: "With the tool · 7 October",
    list: STEPS,
  },
  leaks: LEAKS,
  foundations: FOUNDATIONS,
  software: SOFTWARE,
  tool: {
    flows: [
      { title: "Writing to a firm", nodes: FLOW },
      { title: "When someone answers", nodes: FLOW_ANSWER },
    ],
    lists: TOOL,
    never: NEVER,
    rules: RULES,
  },
  choices: CHOICES,
  stack: STACK,
  risks: RISKS,
  honest: { notBuilt: NOT_BUILT, fix: FIX_YOURSELF },
  price: PRICE,
  proof: DAY_ONE,
  notes: { ...NOTES, proof: NOTES.dayone },
};
